import { Fragment, ReactNode } from 'react'
import styled from 'styled-components'

import { Flex } from '@percent/lemonade'

import { useFilters } from '../../hooks/useFilters'
import {
  DateRangeValue,
  filterFieldKeys,
  FilterDropdownAlign,
  FilterValues,
  ResolvedFilter,
} from '../../types/insights.types'
import {
  InsightsDateRange,
  InsightsDateRangeLabels,
} from '../InsightsDateRange/InsightsDateRange'
import { MultiSelectFilter, MultiSelectFilterLabels } from './MultiSelectFilter'
import { NumberRangeFilter, NumberRangeFilterLabels } from './NumberRangeFilter'

// Link-style reset (no button chrome) — matches the prior donation filter bar.
// Rendered after the filters so it sits inline at the end of the row, rather than
// being prepended and shoving every filter along when a filter becomes active
// (NEX-754).
const ResetButton = styled.button`
  ${({ theme }) => `
    background: none;
    border: none;
    padding: 0.375rem 0.5rem;
    font-size: 0.875rem;
    font-weight: ${theme.fontWeights.medium};
    color: ${theme.colors.primary400};
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  `}
`

export type InsightsFilterControlApi = {
  value: string | string[] | undefined
  setValue: (value: string | string[] | undefined) => void
  isLoading: boolean
  hasError: boolean
}

export type InsightsFiltersProps = {
  testId?: string
  className?: string
  /** Host-localised chrome strings (the lib defaults to neutral English). */
  labels?: MultiSelectFilterLabels & {
    /** Label of the control that clears every filter. */
    reset?: string
    /** Date-range control chrome (trigger label, all-time, custom, apply, clear). */
    dateRange?: InsightsDateRangeLabels
    /** Number-range control chrome (min, max, apply, clear, invalid range). */
    numberRange?: NumberRangeFilterLabels
  }
  /**
   * Which edge every filter's dropdown lines up with. `'end'` (default) hangs it
   * off the trigger's right edge, for a bar sitting at the right of a header;
   * `'start'` off the left edge, for a left-aligned bar such as a report page.
   */
  dropdownAlign?: FilterDropdownAlign
  /**
   * Allowlist of filter keys to render, **in this order** — only these controls
   * show (e.g. `['dateRange', 'activityTags']`). Omit to render every filter the
   * domain returns, in API order. Locked filters never render, even if listed.
   */
  visibleFilters?: string[]
  /** Options for the date-range control, e.g. dropping its "All time" entry. */
  dateRangeOptions?: { showAllTime?: boolean; labelWhenAllTime?: boolean }
  /**
   * Filter keys whose control accepts **one** value at a time: picking an option
   * replaces the previous selection instead of adding to it. The wire format is
   * unchanged (the backend's `stringArray` filters accept a single-element list),
   * so this is presentation only.
   */
  singleSelectFilters?: string[]
  /**
   * Override the control rendered for a given filter key — e.g. to inject a
   * product-specific date-range / preset control for an `isoDatetime` filter.
   * Return `undefined`/`null` to fall back to the default rendering.
   */
  renderControl?: (
    def: ResolvedFilter,
    api: InsightsFilterControlApi
  ) => ReactNode
}

const isMultiSelect = (def: ResolvedFilter) =>
  def.valueType === 'stringArray' || def.valueType === 'enumArray'

// A control is hidden when any of the filter's backing value keys is locked —
// its own `key`, or `period`/`from`/`to` for a dateRange (see `filterFieldKeys`).
const isLocked = (def: ResolvedFilter, lockedFilters: FilterValues) =>
  filterFieldKeys(def).some((key) => key in lockedFilters)

/**
 * The defs to render: locked filters dropped, then (if `visibleFilters` is given)
 * narrowed to those keys in that order — otherwise the domain's API order.
 */
const visibleDefs = (
  filterDefs: ResolvedFilter[],
  lockedFilters: FilterValues,
  visibleFilters?: string[]
): ResolvedFilter[] => {
  const shown = filterDefs.filter((def) => !isLocked(def, lockedFilters))

  if (!visibleFilters) return shown

  const byKey = new Map(shown.map((def) => [def.key, def]))

  return visibleFilters
    .map((key) => byKey.get(key))
    .filter((def): def is ResolvedFilter => def !== undefined)
}

/**
 * The selections a dependent picker is narrowed by: the current value of each filter
 * it `dependsOn`. `{}` for an independent filter, so it fetches unscoped as before.
 */
const scopeFor = (
  def: ResolvedFilter,
  values: FilterValues
): Record<string, string[] | undefined> =>
  Object.fromEntries(
    (def.dependsOn ?? []).map((key) => [
      key,
      Array.isArray(values[key]) ? (values[key] as string[]) : undefined,
    ])
  )

/**
 * Dependent selections to drop alongside a parent picker's change. Only a *narrowing*
 * invalidates them — dropping a programme can strip the templates that belonged to it,
 * whereas adding one (or clearing the filter entirely) only widens what is on offer, so
 * the current selection stays valid and is left alone.
 */
const dependentsToClear = (
  defs: ResolvedFilter[],
  key: string,
  previous: string[],
  next: string[],
  values: FilterValues
): string[] => {
  const narrowed = next.length > 0 && previous.some((v) => !next.includes(v))

  if (!narrowed) return []

  return defs
    .filter(
      (def) =>
        def.dependsOn?.includes(key) &&
        ((values[def.key] as string[] | undefined)?.length ?? 0) > 0
    )
    .map((def) => def.key)
}

/**
 * A generic, zero-config filter bar driven by the domain's `/filters` response:
 * renders a multi-select for each list filter and a reset control when any filter
 * is active. Filters whose UX is product-specific (date ranges, presets) are left
 * to the host via `renderControl`. Values flow through the domain's filter adapter,
 * so `<Insights.Card>` / `<Insights.Chart>` pick them up automatically.
 */
export function InsightsFilters({
  testId = 'insights-filters',
  className,
  labels,
  dropdownAlign = 'end',
  visibleFilters,
  dateRangeOptions,
  singleSelectFilters,
  renderControl,
}: Readonly<InsightsFiltersProps>) {
  const {
    filterDefs,
    values,
    setValue,
    setValues,
    reset,
    hasActiveFilters,
    lockedFilters,
    isLoadingDefs,
    defsError,
  } = useFilters()

  const defs = visibleDefs(filterDefs, lockedFilters, visibleFilters)

  return (
    <Flex
      className={className}
      data-testid={testId}
      align="center"
      wrap="wrap"
      gap="0.75rem"
    >
      {defs.map((def) => {
        const override = renderControl?.(def, {
          value: values[def.key],
          setValue: (next) => setValue(def.key, next),
          isLoading: isLoadingDefs,
          hasError: Boolean(defsError),
        })

        if (override) return <Fragment key={def.key}>{override}</Fragment>

        if (def.valueType === 'dateRange') {
          return (
            <InsightsDateRange
              key={def.key}
              def={def}
              period={values.period as string | undefined}
              from={values.from as string | undefined}
              to={values.to as string | undefined}
              onChange={(value: DateRangeValue) =>
                setValues({
                  period: value && 'period' in value ? value.period : undefined,
                  from: value && 'from' in value ? value.from : undefined,
                  to: value && 'to' in value ? value.to : undefined,
                })
              }
              labels={labels?.dateRange}
              showAllTime={dateRangeOptions?.showAllTime}
              labelWhenAllTime={dateRangeOptions?.labelWhenAllTime}
              dropdownAlign={dropdownAlign}
              testId={`${testId}-${def.key}`}
            />
          )
        }

        if (def.valueType === 'numberRange') {
          return (
            <NumberRangeFilter
              key={def.key}
              def={def}
              values={values}
              onChange={setValues}
              labels={labels?.numberRange}
              dropdownAlign={dropdownAlign}
              testId={`${testId}-${def.key}`}
            />
          )
        }

        if (!isMultiSelect(def)) return null

        const selected = (values[def.key] as string[] | undefined) ?? []

        // Writing the dependents' reset in the SAME update as the parent keeps the two
        // from racing (a separate setValue would be built from a stale value set).
        const handleChange = (next: string[]) => {
          const cleared = dependentsToClear(
            defs,
            def.key,
            selected,
            next,
            values
          )
          const value = next.length > 0 ? next : undefined

          if (cleared.length === 0) {
            setValue(def.key, value)

            return
          }

          setValues({
            [def.key]: value,
            ...Object.fromEntries(cleared.map((key) => [key, undefined])),
          })
        }

        return (
          <MultiSelectFilter
            key={def.key}
            def={def}
            isSingleSelect={singleSelectFilters?.includes(def.key) ?? false}
            selected={selected}
            scope={scopeFor(def, values)}
            onChange={handleChange}
            isLoadingDefs={isLoadingDefs}
            hasError={Boolean(defsError)}
            labels={labels}
            dropdownAlign={dropdownAlign}
            testId={`${testId}-${def.key}`}
          />
        )
      })}

      {hasActiveFilters && (
        <ResetButton
          type="button"
          onClick={reset}
          data-testid={`${testId}-reset`}
        >
          {labels?.reset ?? 'Reset'}
        </ResetButton>
      )}
    </Flex>
  )
}
